import moment from 'moment';
import { roundNumber } from './maths';

export const currentYear = new Date().getFullYear();

const nbYears = 5;
export const years = [...Array(nbYears).keys()]
  .map((e) => currentYear - e)
  .toReversed();

export const WEEKNUMBER = 52;

/**
 * in mobile mode
 */
export const maxWeeksToDisplay = 6;

/**
 * jeudi 1er janvier -> renvoie 5
 *
 * samedi 1er janvier -> renvoie 3
 */
export const getFirstDayOYear = (date = new Date()) => {
  const startDate = new Date(date.getFullYear(), 0, 1); // 1er janvier
  let monday = startDate;
  while (monday.getDay() !== 1) {
    monday.setDate(monday.getDate() + 1);
  }
  const janX = monday.getDate();
  return janX;
};

const janX = getFirstDayOYear(); // first day of January that must belong to the first week of the year
const dow = 1; // 1 is Monday

// https://momentjs.com/docs/#/i18n/adding-locale/
moment.locale('fr', {
  week: {
    dow,
    doy: 7 + dow - janX,
  },
});

export const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const getTime = new Intl.DateTimeFormat('fr', {
  timeStyle: 'short',
});

export const getDate = new Intl.DateTimeFormat('fr', {
  dateStyle: 'short',
});

export const shortMonthNames = monthNames.map((month) =>
  month.slice(0, 3).toUpperCase()
);

export const weekToMonth = (week: number) => {
  // On calcule le nombre de jours dans la semaine donnée
  const days = week * 7;

  // On calcule le nombre de mois complets à partir du nombre de jours
  const months = Math.floor(days / 31);

  // On calcule le nombre de jours restants
  const remainingDays = days % 31;

  if (months === 11) return shortMonthNames[months];

  return remainingDays < 8
    ? shortMonthNames[months - 1]
    : shortMonthNames[months];
};

export const formatWeekNumber = (weekNumber: number) => {
  if (weekNumber % 2 === 1)
    // %2 -> month displayed every 2 weeks
    // 1 -> month displayed below 1st week of the month
    return getMondayFormattedDate(weekNumber);
  else return '';
};

// moment().week():
// moment().isoWeek():
export const getWeekNumber = (date: string) => {
  const week = moment(date).week();
  const monthNumber = new Date(date).getMonth();
  // exception pour les premiers jours de l'année encore en semaine 52/53 de l'année précédente
  if (week >= WEEKNUMBER && monthNumber === 0) return 0;
  return week;
};

export const lastWeekNumber = getWeekNumber(new Date().toDateString());

/**
 *
 * @param date
 * @returns timestamp in seconds
 */
export const getTimeStamp = (date: string) => {
  return moment(date).unix();
};

/**
 *
 * @param week - number
 * @returns - formatted string date - 'DEC 19' - premier jour de la semaine donnée (lundi)
 */
export const getMondayFormattedDate = (week: number) => {
  const date = getFirstDayFromWeek(week);
  const day = date.getDay();
  // Sunday - Saturday : 0 - 6
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
  const newDate = new Date(date.setDate(diff));
  const monthStr = shortMonthNames[newDate.getMonth()];
  const dayNum = newDate.getDate();
  return `${monthStr} ${dayNum}`;
};

/**
 * @param week - number (3)
 * @param year - number (2024)
 * @returns JS object date - 'Sun Jan 15 2023 00:00:00 GMT+0100'
 */
export const getFirstDayFromWeek = (week: number, year = currentYear) => {
  const day = (week - 1) * 7 + janX;
  return new Date(year, 0, day);
};

const oneYearDuration = 365 * 24 * 3600; // number of seconds in a year
const rollingYearAfter = Date.now() / 1000 - oneYearDuration;

export const getRangeDateParams = (year: number) => {
  const isCurrentYear = year === years.at(-1);

  // An epoch timestamp to use for filtering activities that have taken place after a certain time in seconds.
  // first day of the year
  const after = Date.parse(`01/01/${year}`) / 1000; // MM/DD/YYYY, in seconds

  // An epoch timestamp to use for filtering activities that have taken place before a certain time in seconds.
  const before = isCurrentYear
    ? Date.now() / 1000 // today
    : Date.parse(`12/31/${year}`) / 1000; // in seconds

  return {
    after,
    before,
  };
};

export const formatDate = (number: number) => {
  if (number < 10) return `0${number}`;
  else return `${number}`;
};

/**
 *
 * @param movingTime seconds
 * @returns hours, minuts, seconds
 */
export const getDuration = (movingTime: number) => {
  const hours = formatDate(Math.floor(movingTime / 3600));
  const minuts = formatDate(Math.floor((movingTime / 60) % 60));
  const seconds = formatDate(movingTime % 60);
  return { hours, minuts, seconds };
};

export const formatHours = (hours: number): string => {
  if (hours === undefined) {
    throw new Error('hours is not defined');
  }
  const minuts = Math.floor((hours - Math.trunc(hours)) * 60);
  return `${Math.trunc(hours)} h ${minuts}`;
};

/**
 *
 * @param speed min (decimal)
 * @returns min:sec
 */
export const getMinutsSecondsSpeed = (speed: number) => {
  const minuts = Math.trunc(speed);
  const seconds = (speed - minuts) * 60;
  return roundNumber(minuts + seconds / 100);
};

/**
 *
 * @param startDateLocal "2023-01-01T11:47:54Z"
 * @returns day number in the year
 */
export const getDayYearNumberFromDate = (startDateLocal: string) => {
  const date = new Date(startDateLocal); // date de l'activité
  const start = new Date(date.getFullYear(), 0, 0); // 1er janvier
  const diff = date.getTime() - start.getTime(); // nombre de jours écoulés depuis 1er janvier
  const day = Math.floor(diff / 1000 / 3600 / 24); // conversion ms en jours
  return day;
};

export const getDayMonth = (day: number): number => {
  const date = new Date();
  return new Date(date.getFullYear(), 0, day).getDate();
};

export const getNumberOfWeeksElapsed = (date?: string): number => {
  const todayDate = date ?? new Date().toString();
  const nbDays = getDayYearNumberFromDate(todayDate);
  return nbDays / 7;
};

/** renvoie le jour de la semaine en chiffre - 0 - 6 */
export const getDayWeek = (startDateLocal: string): number => {
  const date = new Date(startDateLocal);
  const day = date.getDay() - 1;
  return day === -1 ? 6 : day;
};

/** convertit la date en heures (décimales) */
export const getHour = (startDateLocal: string): number => {
  const date = new Date(startDateLocal);
  return date.getHours() + date.getMinutes() / 60;
};
